import axios, { CancelToken } from 'axios';
import { getApi } from '../api';
import { handleError, handleResponse } from './response';

/** @param {string} resource */
const getAll = async (resource: string, cancelToken?: CancelToken) => {
  try {
    const api = await getApi();
    const response = await api.get(
      `${resource}`,
      cancelToken && {
        cancelToken,
      },
    );
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

const getSingle = async (resource: string, id: number | string, cancelToken?: CancelToken) => {
  try {
    const api = await getApi();
    const response = await api.get(
      `${resource}/${id}`,
      cancelToken && {
        cancelToken,
      },
    );
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

const get = async (resource: string, params: any) => {
  try {
    const api = await getApi();
    const response = await api.get(`${resource}`, { params });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

const post = async (resource: string, model: any) => {
  try {
    const api = await getApi();
    const response = await api.post(`${resource}`, model);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

const getWithBody = async (resource: string, params?: any) => {
  try {
    const api = await getApi();
    const response = await api.get(`${resource}`, params);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

const postWithFile = async (
  resource: string,
  file: any,
  fileInputName: string,
  data?: { [key: string | number]: any },
  overridePutMethod = false,
) => {
  try {
    const api = await getApi();
    const formData = new FormData();
    formData.append(fileInputName, file);
    if (overridePutMethod) {
      formData.append('_method', 'PUT');
    }

    if (data) {
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });
    }

    const response = await api.post(`${resource}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

const put = async (resource: string, id: number | string, model: any) => {
  try {
    const api = await getApi();
    const response = await api.put(`${resource}/${id}`, model);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

const putWithFile = async (
  resource: string,
  id: number | string,
  file: File,
  fileInputName: string,
  data?: Record<string, unknown>,
) => {
  try {
    const api = await getApi();
    const formData = new FormData();
    formData.append(fileInputName, file);
    formData.append('_method', 'PUT');

    Object.entries(data ?? {}).forEach(([key, value]) => {
      formData.append(key, String(value));
    });

    const response = await api.post(`${resource}/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

const remove = async (resource: string, id: number | string, model?: any) => {
  try {
    const api = await getApi();
    const response = await api.delete(`${resource}/${id}`, model);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

const removeByBody = async (resource: string, model?: any) => {
  try {
    const api = await getApi();
    const response = await api.delete(`${resource}`, model);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

const removeMany = async (resource: string, id: Array<number> | Array<string>) => {
  try {
    const api = await getApi();
    const response = await api.delete(`${resource}/${id}`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

const axiosApi = axios;

const apiProvider = {
  get,
  getAll,
  getSingle,
  post,
  put,
  putWithFile,
  remove,
  removeMany,
  removeByBody,
  axiosApi,
  getWithBody,
  postWithFile,
};

export default apiProvider;
